"use client";

import { useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------ */
/*  Props                                                             */
/* ------------------------------------------------------------------ */

interface DynamicIslandProps {
  /** Text to display inside the island pill */
  message: string;
  /** Whether the island is visible */
  visible: boolean;
  /** Called when the island is dismissed (manual close or auto-dismiss) */
  onDismiss?: () => void;
  /** Auto-dismiss timeout in ms (default 5000) */
  autoDismissMs?: number;
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export default function DynamicIsland({
  message,
  visible,
  onDismiss,
  autoDismissMs = 5000,
}: DynamicIslandProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    clearTimer();

    if (visible && onDismiss && autoDismissMs > 0) {
      timerRef.current = setTimeout(() => {
        onDismiss();
      }, autoDismissMs);
    }

    return clearTimer;
  }, [visible, onDismiss, autoDismissMs, clearTimer]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -40, scale: 0.85 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -40, scale: 0.85 }}
          transition={{
            type: "spring",
            stiffness: 420,
            damping: 30,
            mass: 0.9,
          }}
          className="fixed top-6 left-1/2 -translate-x-1/2 z-50"
        >
          <div
            className={cn(
              "flex items-center gap-3 px-5 py-3",
              "bg-black/90 backdrop-blur-xl",
              "border border-white/10",
              "rounded-full",
              "shadow-[0_12px_40px_rgba(0,0,0,0.55),0_0_0_0.5px_rgba(255,255,255,0.08)_inset]"
            )}
          >
            {/* ---- Gold dot indicator ---- */}
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-gold" />
            </span>

            {/* ---- Message ---- */}
            <span className="text-white text-sm font-medium whitespace-nowrap select-none">
              {message}
            </span>

            {/* ---- Manual dismiss ---- */}
            {onDismiss && (
              <button
                onClick={onDismiss}
                className={cn(
                  "ml-1 p-0.5 rounded-full",
                  "text-white/40 hover:text-white",
                  "hover:bg-white/10",
                  "transition-colors duration-200",
                  "focus:outline-none"
                )}
                aria-label="Tutup notifikasi"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
